import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Badge, Box, Button, Card, CardActionArea, CardContent, Chip,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, IconButton, MenuItem, Paper, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  collection, query, where, getDocs, addDoc, doc, updateDoc, onSnapshot, Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { stripUndefined } from '../utils/firestoreSafe';
import { getLocalizedProductName } from '../utils/localization';
import { includedProductIdsForTable } from '../utils/planInclusion';
import { resolveTaxRate } from '../utils/taxRate';

/**
 * 代理注文（PC版）
 *
 * 夜職では黒服・レジ・カウンター内のスタッフが注文を入力する。
 * 客がボタンを押すことはない。
 * これまでスタッフ入力は Flutter アプリにしか無く、
 * PC 1台で回している店（カウンター中心のスナックなど）は
 * 卓に紐づけて伝票を育てられなかった。
 *
 * 書き込む形は Flutter の代理注文と揃えてある。
 * どちらから入力しても、会計・バック・給与は同じ経路を通る。
 */

interface TableRow {
  id: string;
  tableNumber: string;
  name?: string;
  capacity?: number;
  status?: string;
}

interface ProductRow {
  id: string;
  name: string;
  nameEn?: string;
  nameTh?: string;
  price: number;
  categoryId?: string;
  costInfo?: { cost?: number; baseBackRate?: number } | null;
  isActive?: boolean;
  sortOrder?: number;
}

interface CategoryRow { id: string; name: string; sortOrder?: number }
interface StaffRow { id: string; name: string }

interface CartLine {
  key: string;
  product: ProductRow;
  quantity: number;
  /** この商品を取ったキャスト。複数なら均等割り。 */
  staff: StaffRow[];
  note?: string;
}

const yen = (n: number) => `¥${Math.round(n).toLocaleString()}`;

const ProxyOrder: React.FC = () => {
  const { shop, user } = useAuth();
  const [step, setStep] = useState<'table' | 'menu'>('table');
  // この卓の飲み放題・コースに含まれる商品。
  // 以前は送信時にしか判定しておらず、画面には定価が出ていた。
  // スタッフが見る金額と、実際に請求する金額が食い違う。
  const [includedIds, setIncludedIds] = useState<Set<string>>(new Set());
  const [tables, setTables] = useState<TableRow[]>([]);
  const [selectedTable, setSelectedTable] = useState<TableRow | null>(null);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [staffList, setStaffList] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [categoryTab, setCategoryTab] = useState(0);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  /** 卓に付いた既定のキャスト。以降の商品にこの人が初期選択される。 */
  const [defaultStaff, setDefaultStaff] = useState<StaffRow[]>([]);

  // ---- 読み込み ----
  useEffect(() => {
    if (!shop) return;
    setLoading(true);
    const unsubs: (() => void)[] = [];

    unsubs.push(onSnapshot(
      query(collection(db, 'tables'), where('shopId', '==', shop.id), where('isActive', '==', true)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as TableRow[];
        list.sort((a, b) =>
          String(a.tableNumber).localeCompare(String(b.tableNumber), 'ja', { numeric: true }));
        setTables(list);
        setLoading(false);
      },
      (e) => { setError(e.message); setLoading(false); }
    ));

    (async () => {
      try {
        const [catSnap, prodSnap, staffSnap] = await Promise.all([
          getDocs(query(collection(db, 'productCategories'), where('shopId', '==', shop.id))),
          getDocs(query(collection(db, 'products'), where('shopId', '==', shop.id))),
          getDocs(query(collection(db, 'employees'), where('shopId', '==', shop.id), where('status', '==', 'active'))),
        ]);
        const cats = catSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((c: any) => c.isActive !== false) as CategoryRow[];
        cats.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setCategories(cats);

        const prods = prodSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((p: any) => p.isActive !== false) as ProductRow[];
        prods.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setProducts(prods);

        setStaffList(staffSnap.docs.map((d) => {
          const x = d.data() as any;
          const full = [x.personalInfo?.lastName, x.personalInfo?.firstName].filter(Boolean).join(' ').trim();
          return {
            id: d.id,
            name: x.displayName?.trim() || full || `（名前未設定 ${x.employeeCode || d.id.slice(0, 6)}）`,
          };
        }).sort((a, b) => a.name.localeCompare(b.name, 'ja')));
      } catch (e: any) {
        setError(e?.message ?? '読み込みに失敗しました');
      }
    })();

    return () => unsubs.forEach((u) => u());
  }, [shop]);

  const visibleProducts = useMemo(() => {
    const cat = categories[categoryTab];
    if (!cat) return products;
    return products.filter((p) => p.categoryId === cat.id);
  }, [products, categories, categoryTab]);

  // 卓を選んだ時点で、その卓の飲み放題に含まれる商品を読む。
  useEffect(() => {
    if (!shop || !selectedTable) { setIncludedIds(new Set()); return; }
    let alive = true;
    (async () => {
      try {
        const [planSnap, poSnap] = await Promise.all([
          getDocs(query(collection(db, 'drinkFoodPlans'), where('shopId', '==', shop.id))),
          getDocs(query(
            collection(db, 'planOrders'),
            where('shopId', '==', shop.id),
            where('tableId', '==', selectedTable.id),
            where('status', 'in', ['active', 'last_order_notified']),
          )),
        ]);
        if (!alive) return;
        setIncludedIds(includedProductIdsForTable(
          poSnap.docs.map((d) => d.data() as any),
          planSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })),
        ));
      } catch (e) {
        console.error('飲み放題の対象商品を取れませんでした:', e);
      }
    })();
    return () => { alive = false; };
  }, [shop, selectedTable]);

  /** 画面に出す1点の値段。飲み放題に含まれていれば0円。 */
  const priceOf = useCallback(
    (p: { id: string; price: number }) => (includedIds.has(p.id) ? 0 : p.price),
    [includedIds]
  );

  const subtotal = useMemo(
    () => cart.reduce((s, l) => s + priceOf(l.product) * l.quantity, 0),
    [cart, priceOf]
  );
  const cartCount = useMemo(() => cart.reduce((s, l) => s + l.quantity, 0), [cart]);

  // ---- カート操作 ----
  const addToCart = (product: ProductRow) => {
    setCart((prev) => {
      // 同じ商品・同じ担当の行はまとめる
      const staffKey = defaultStaff.map((s) => s.id).sort().join(',');
      const idx = prev.findIndex(
        (l) => l.product.id === product.id && l.staff.map((s) => s.id).sort().join(',') === staffKey
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...prev, {
        key: `${product.id}_${staffKey}_${Date.now()}`,
        product, quantity: 1, staff: [...defaultStaff],
      }];
    });
  };

  const changeQuantity = (key: string, delta: number) => {
    setCart((prev) => prev
      .map((l) => (l.key === key ? { ...l, quantity: l.quantity + delta } : l))
      .filter((l) => l.quantity > 0));
  };

  const setLineStaff = (key: string, ids: string[]) => {
    setCart((prev) => prev.map((l) => l.key === key
      ? { ...l, staff: ids.map((id) => staffList.find((s) => s.id === id)).filter(Boolean) as StaffRow[] }
      : l));
  };

  // ---- 送信 ----
  const submit = async () => {
    if (!shop || !selectedTable || cart.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const now = Timestamp.now();
      // 税は店舗設定に従う
      const taxRate = resolveTaxRate(shop);

      // 表示に使っているものと同じ集合を使う。
      // 送信時にもう一度読むと、画面の金額と送る金額が食い違いうる。
      const includedForSubmit = includedIds;

      const orderItems = cart.map((l, i) => {
        const p = l.product;
        const share = l.staff.length > 0 ? 1 / l.staff.length : 0;
        return stripUndefined({
          id: `${Date.now()}_${i}_${p.id}`,
          productId: p.id,
          productName: p.name,
          productNameEn: p.nameEn,
          productNameTh: p.nameTh,
          categoryId: p.categoryId,
          // 飲み放題・コースに含まれる商品は請求しない。
          // 定価は originalPrice に残すので、あとから
          // 「本来いくらの酒が出たか」は分かる。
          price: includedForSubmit.has(p.id) ? 0 : p.price,
          unitPrice: includedForSubmit.has(p.id) ? 0 : p.price,
          originalPrice: p.price,
          includedInPlan: includedForSubmit.has(p.id),
          quantity: l.quantity,
          subtotal: includedForSubmit.has(p.id) ? 0 : p.price * l.quantity,
          selectedOptions: [],
          status: 'pending',
          itemStatus: 'pending',
          notes: l.note ?? '',
          // 原価はバックの母数に効く
          ...(p.costInfo?.cost != null
            ? { costInfo: { cost: p.costInfo.cost, baseBackRate: p.costInfo.baseBackRate ?? 0 } }
            : {}),
          // どのキャストが取ったか。ここが無いとバックの行き先が決まらない。
          ...(l.staff.length > 0
            ? {
                salesStaff: l.staff.map((s) => ({
                  staffId: s.id,
                  staffName: s.name,
                  shareRate: share,
                  ...(p.costInfo?.baseBackRate != null ? { backRate: p.costInfo.baseBackRate } : {}),
                })),
              }
            : {}),
        });
      });

      const sub = orderItems.reduce((s: number, it: any) => s + it.subtotal, 0);
      const tax = Math.floor(sub * taxRate);

      // Flutter の代理注文と同じ形にする。
      // どちらから入力しても会計・バック・給与は同じ経路を通る。
      const orderData = stripUndefined({
        shopId: shop.id,
        orderType: 'dine_in',
        tableId: selectedTable.id,
        tableNumber: selectedTable.tableNumber,
        items: orderItems,
        subtotal: sub,
        tax,
        total: sub + tax,
        ...(customerName.trim() ? { customerName: customerName.trim(), customerNote: customerName.trim() } : {}),
        status: 'pending',
        paymentStatus: 'unpaid',
        orderedBy: {
          isStaffOrder: true,
          userId: user?.uid ?? 'staff',
          staffId: user?.uid,
          staffName: user?.email ?? '店舗スタッフ',
          ...(customerName.trim() ? { userName: customerName.trim() } : {}),
        },
        createdAt: now,
        updatedAt: now,
        orderedAt: now,
      });

      await addDoc(collection(db, 'orders'), orderData);

      // 卓を使用中にする
      await updateDoc(doc(db, 'tables', selectedTable.id), {
        status: 'occupied',
        sessionStartedAt: now,
        updatedAt: now,
      });

      const unassigned = cart.filter((l) => l.staff.length === 0).length;
      setInfo(
        `${selectedTable.tableNumber}番卓に ${cartCount} 点を通しました（${yen(sub + tax)}）。` +
        (unassigned > 0 ? ` 担当が付いていない商品が ${unassigned} 行あります。` : '')
      );
      setCart([]);
      setCartOpen(false);
    } catch (e: any) {
      setError(e?.message ?? '注文の送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- 卓選択 ----
  if (step === 'table') {
    return (
      <Box>
        <Typography variant="h5" fontWeight="bold" gutterBottom>代理注文</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          卓を選んで注文を通します。商品ごとに担当キャストを付けられます。
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : tables.length === 0 ? (
          <Alert severity="warning">
            <AlertTitle>卓が登録されていません</AlertTitle>
            テーブル管理から卓を登録すると、ここに出ます。
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {tables.map((t) => {
              const occupied = t.status === 'occupied';
              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={t.id}>
                  <Card variant="outlined" sx={{
                    borderColor: occupied ? 'error.main' : 'divider',
                    borderWidth: occupied ? 2 : 1,
                  }}>
                    <CardActionArea onClick={() => { setSelectedTable(t); setStep('menu'); }}>
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="h4" fontWeight="bold">{t.tableNumber}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t.name ?? `${t.tableNumber}番卓`}
                        </Typography>
                        <Chip size="small" sx={{ mt: 1 }}
                          label={occupied ? '使用中' : '空席'}
                          color={occupied ? 'error' : 'default'} />
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    );
  }

  // ---- メニュー ----
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => { setStep('table'); setCart([]); }}><ArrowBackIcon /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {selectedTable?.tableNumber}番卓へ注文
            </Typography>
            <Typography variant="body2" color="text.secondary">
              商品を押すとカートに入ります
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={
          <Badge badgeContent={cartCount} color="error"><ShoppingCartIcon /></Badge>
        } disabled={cart.length === 0} onClick={() => setCartOpen(true)}>
          カートを確認（{yen(subtotal)}）
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" label="お客様名（任意）" value={customerName}
            onChange={(e) => setCustomerName(e.target.value)} sx={{ minWidth: 200 }} />
          <TextField select size="small" label="この卓の担当キャスト"
            value={defaultStaff.map((s) => s.id)}
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as string[]).map((id) => staffList.find((s) => s.id === id)?.name).filter(Boolean).join(' / ') || '未選択' }}
            onChange={(e) => {
              const ids = e.target.value as unknown as string[];
              setDefaultStaff(ids.map((id) => staffList.find((s) => s.id === id)).filter(Boolean) as StaffRow[]);
            }}
            sx={{ minWidth: 260 }}
            helperText="ここで選ぶと、以降カートに入れる商品に自動で付きます"
          >
            {staffList.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
        </Stack>
        {defaultStaff.length === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            担当キャストが未選択です。このまま通すと、その商品は誰のバックにもなりません。
            会計時に「担当未設定」として残ります。
          </Alert>
        )}
      </Paper>

      {categories.length > 0 && (
        <Tabs value={categoryTab} onChange={(_, v) => setCategoryTab(v)}
          variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
          {categories.map((c) => <Tab key={c.id} label={c.name} />)}
        </Tabs>
      )}

      <Grid container spacing={1.5}>
        {visibleProducts.map((p) => (
          <Grid item xs={6} sm={4} md={3} key={p.id}>
            <Card variant="outlined">
              <CardActionArea onClick={() => addToCart(p)}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ minHeight: 40 }}>
                    {/* 商品マスタの項目は name。
                        getLocalizedProductName は注文明細用（productName）なので
                        ここで使うと名前が空になり、価格だけのカードになる。 */}
                    {p.name}
                  </Typography>
                  {includedIds.has(p.id) ? (
                    <Typography variant="h6" color="success.main">
                      飲み放題
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        （{yen(p.price)}）
                      </Typography>
                    </Typography>
                  ) : (
                    <Typography variant="h6" color="primary">{yen(p.price)}</Typography>
                  )}
                  {(p.costInfo?.cost ?? 0) === 0 && (
                    <Typography variant="caption" color="warning.main">原価未設定</Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      {visibleProducts.length === 0 && (
        <Alert severity="info">このカテゴリに商品がありません。</Alert>
      )}

      {/* カート */}
      <Dialog open={cartOpen} onClose={() => !submitting && setCartOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedTable?.tableNumber}番卓へ通す
          <Typography variant="body2" color="text.secondary">
            {cartCount} 点 / 小計 {yen(subtotal)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {cart.map((l) => (
              <Box key={l.key}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {l.product.name}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {yen(priceOf(l.product))}
                    </Typography>
                  </Typography>
                  <IconButton size="small" onClick={() => changeQuantity(l.key, -1)}><RemoveIcon fontSize="small" /></IconButton>
                  <Typography sx={{ minWidth: 24, textAlign: 'center' }}>{l.quantity}</Typography>
                  <IconButton size="small" onClick={() => changeQuantity(l.key, 1)}><AddIcon fontSize="small" /></IconButton>
                  <Typography sx={{ minWidth: 76, textAlign: 'right' }}>
                    {yen(priceOf(l.product) * l.quantity)}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => changeQuantity(l.key, -l.quantity)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <TextField
                  select size="small" fullWidth label="この商品の担当"
                  value={l.staff.map((s) => s.id)}
                  SelectProps={{ multiple: true, renderValue: (v: any) =>
                    (v as string[]).map((id) => staffList.find((s) => s.id === id)?.name).filter(Boolean).join(' / ') || '担当なし' }}
                  onChange={(e) => setLineStaff(l.key, e.target.value as unknown as string[])}
                  sx={{ mt: 0.5 }}
                  helperText={l.staff.length > 1 ? `${l.staff.length}人で均等割り` : undefined}
                >
                  {staffList.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </TextField>
                <Divider sx={{ mt: 1.5 }} />
              </Box>
            ))}
          </Stack>

          {cart.some((l) => l.staff.length === 0) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              担当が付いていない商品があります。そのぶんは誰のバックにもならず、
              会計時に「担当未設定」として残ります。
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCartOpen(false)} disabled={submitting}>戻る</Button>
          <Button variant="contained" onClick={submit} disabled={submitting || cart.length === 0}>
            {submitting ? '送信中…' : 'この内容で通す'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProxyOrder;
